import React, { useState } from "react";
import { Mail, MessageSquare, User } from "lucide-react";
import { toast } from "sonner";
import { Staff } from "../../../Table/StaffTable";
import PersonalInfo from "../../../../components/PersonalInfo";
import PermissionsContent from "../../../../components/PermissionsContent";
import {
    useGetRolesQuery,
    useUpdateStaffRoleMutation,
    useUpdateStaffUserMutation,
    useGetRolePermissionsQuery,
} from "../../../../redux/services/staffApi";

interface StaffProfileProps {
    staff: Staff;
    onBack?: () => void;
}

const StaffProfile: React.FC<StaffProfileProps> = ({ staff, onBack }) => {
    const [profileImage, setProfileImage] = useState<string | null>(
        "profileImage" in staff ? staff.profileImage || null : null
    );
    const [activeTab, setActiveTab] = useState<"personal" | "permissions">("personal");
    const [selectedRole, setSelectedRole] = useState<string>(staff.role_id || "");

    const [updateStaffUser] = useUpdateStaffUserMutation();
    const [updateRole] = useUpdateStaffRoleMutation();
    const { data: rolesData, isLoading: rolesLoading } = useGetRolesQuery();

    // determine the role id to use for fetching permissions (use selectedRole when changed)
    const roleId =
        selectedRole ||
        staff.role_id ||
        rolesData?.find((r: { id: string; name: string }) => r.name === staff.role)?.id;

    const { data: rolePermissionsData, isLoading: rolePermsLoading } = useGetRolePermissionsQuery(
        roleId?.toString() || "",
        { skip: !roleId }
    );

    const rolePermissions = rolePermissionsData?.data?.data?.permissions || [];
    console.log("rolePermissions", rolePermissions);
    const roleList = Array.isArray(rolesData) ? rolesData : [];

    // profile image change handler
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => reader.result && setProfileImage(reader.result as string);
        reader.readAsDataURL(file);

        const formData = new FormData();
        formData.append("profile_image", file);

        try {
            await updateStaffUser({ userId: staff.id, body: formData }).unwrap();
            toast.success("Profile image updated successfully");
        } catch {
            toast.error("Failed to update profile image");
        }
    };

    // role change handler
    const handleRoleChange = async (roleId: string) => {
        setSelectedRole(roleId);
        try {
            await updateRole({ staffId: staff.id.toString(), role_id: roleId }).unwrap();
            toast.success("Role updated successfully");
        } catch {
            toast.error("Failed to update role");
        }
    };

    const renderInput = (label: string, value?: string | number) => (
        <div className="flex flex-col">
            <label className="text-gray-600 mb-1 text-sm">{label}</label>
            <input
                type="text"
                value={value ?? ""}
                readOnly
                className="p-3 border rounded-lg bg-gray-100 cursor-not-allowed focus:outline-none"
            />
        </div>
    );

    return (
        <div className="bg-white rounded-2xl shadow p-6 space-y-6">
            {onBack && (
                <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:underline no-print"
                >
                    ← Back
                </button>
            )}

            {/* header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-[#B4C0D8] rounded-lg p-5 gap-4">
                <div className="flex items-start md:items-center gap-4 flex-1">
                    <div className="relative w-20 h-20 shrink-0">
                        {profileImage ? (
                            <img
                                src={profileImage}
                                alt={staff.name || ""}
                                className="w-full h-full rounded-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                <User size={48} />
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                        />
                    </div>

                    <div className="flex flex-col w-full">
                        <h2 className="text-lg md:text-[24px] font-bold text-[#00032A]">
                            {staff.name || `${staff.firstName || ""} ${staff.lastName || ""}`}
                        </h2>
                        <span className="block md:hidden mt-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm w-fit">
                            {staff.role || ""}
                        </span>
                        <div className="flex gap-2 mt-3 w-full">
                            <button className="flex items-center justify-center gap-2 px-3 py-2 bg-[#6226EF] text-white rounded-lg text-sm w-1/2 md:w-auto">
                                <MessageSquare size={16} /> Message
                            </button>
                            <button className="flex items-center justify-center gap-2 px-3 py-2 bg-[#26A65D] text-white rounded-lg text-sm w-1/2 md:w-auto">
                                <Mail size={16} /> Email
                            </button>
                        </div>
                    </div>
                </div>

                <div className="hidden md:block self-start md:self-center">
                    <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm text-center break-words max-w-[150px] md:max-w-none">
                        {staff.role || ""}
                    </span>
                </div>
            </div>

            {/* tabs */}
            <div className="border-b">
                <nav className="flex gap-6 text-sm">
                    {["personal", "permissions"].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab as "personal" | "permissions")}
                            className={`pb-2 ${activeTab === tab
                                ? "border-b-2 border-blue-600 text-blue-600 font-medium"
                                : "text-gray-500"
                                }`}
                        >
                            {tab === "personal" ? "Personal Information" : "Permissions"}
                        </button>
                    ))}
                </nav>
            </div>

            {/* tab content */}
            {activeTab === "personal" && (
                <PersonalInfo
                    staff={staff}
                    renderInput={renderInput}
                    roleList={roleList}
                    selectedRole={selectedRole}
                    onRoleChange={handleRoleChange}
                    rolesLoading={rolesLoading}
                />
            )}

            {activeTab === "permissions" && (
                <PermissionsContent mode="staff" staff={staff} permissions={rolePermissions} loading={rolePermsLoading} />
            )}

        </div>
    );
};

export default StaffProfile;
